import React, { memo, ReactNode } from 'react';

import PopConfirm from '@plasma/ui.feedback.pop-confirm';
import Button from '@plasma/ui.general.button';
import Input from '@plasma/ui.input.input';
import Select from '@plasma/ui.input.select';
import Modal from '@plasma/ui.layout.modal';
import ThemeProvider from '@plasma/ui.themes.theme-provider';
import { ThemeOverride } from '@plasma/ui.utils.types';

import LoadingComponent from '../../components/Loding-component/LoadingComponent';
import { TopBarThemed } from '../../components/top-bar-themed';
import { GuideRequirement } from '../../types/guideMasterDatatype';
import { GuideInstalledPositionForm } from './components/guide-installed-position-form';
import useGuideOrderHook from './guide-order.hook';
import {
  BlockHeader,
  CenterIllustrationColumn,
  ComponentColumn,
  DashboardGrid,
  DashboardColumn,
  FieldWrapper,
  ComponentTable,
  CodeLink,
  ForcedSetupContainer,
  ForcedSetupText,
  ForcedSetupTitle,
  Label,
  NoDataMessage,
  StandLayoutWrapper,
  SidebarSection,
  StandNavigationHeader,
  SuggestionsContainer,
  SuggestionCard,
  SuggestionHeader,
  GuideTitle,
  ChangeWarningTag,
  RankBadge,
  PdfModalContent,
  GuideOrderContainer,
} from './guide-order.styles';
import themes from './themes';

export interface GuideOrderProps {
  children?: ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

type Props = GuideOrderProps & ThemeOverride;

function GuideOrder(props: Props) {
  const { children, className, style, size, themeMode } = props;

  const {
    t,
    isAdmin,
    pageLoading,
    getProfileData,
    getAllGuidData,
    activeStandSerialRecommendation,
    getAllGuideStand,
    selectedStand,
    isInstalledPositionsEmpty,
    targetProfile,
    currentProfile,
    iFrameSrc,
    activeStandTargetComponentData,
    showConsumableCodeDrawing,
    selectedCode,
    showInstalledPositionForm,
    handleOpenInstalledPositionForm,
    handleCloseInstalledPositionForm,
    handleInstalledPositionsSaved,
    handleTargetProfileSelection,
    handleConsumableCodeClick,
    handleDeletePDF,
    handleStandSelection,
    handleDrawingModalCancel,
    expandedSerialCode,
    setExpandedSerialCode,
    selectedEntrySerial,
    setSelectedEntrySerial,
    selectedExitSerial,
    setSelectedExitSerial,
    handleClearEntrySelection,
    handleClearExitSelection,
    savedSelections,
    saveSerialSelection,
    generateGuideReport,
    guideReportSrc,
    handleGuideReportCancel,
  } = useGuideOrderHook(props);

  const renderCodeCell = (code: string | undefined | null) => {
    if (!code) return '-';

    const hasDrawing = getAllGuidData?.some((item: any) => item.docfor?.trim() === code.trim() && item.guid?.[0]);
    return hasDrawing ? (
      <CodeLink
        href="#"
        onClick={(e) => {
          e.preventDefault();
          handleConsumableCodeClick(code);
        }}
      >
        {code}
      </CodeLink>
    ) : (
      <span>{code}</span>
    );
  };

  const addSerialToMap = (serialsMap: Map<string, any>, serialData: any, isRecommended = false) => {
    if (!serialData) return;
    const code = serialData?.serial?.componentCode;
    const components = serialData?.serialComponents;

    if (code) {
      const existing = serialsMap.get(code);
      serialsMap.set(code, {
        code,
        isRecommended: isRecommended || existing?.isRecommended || false,
        serialComponents: components,
      });
    }
  };

  const renderSuggestionsTable = (recommendation: any, titleKey: string, type: 'entry' | 'exit') => {
    const selectedSerial = type === 'entry' ? selectedEntrySerial : selectedExitSerial;
    const setSelectedSerial = type === 'entry' ? setSelectedEntrySerial : setSelectedExitSerial;

    const changeAnalysisData = recommendation?.changeAnalysis;
    const totalChangesCount = changeAnalysisData?.totalChangesCount ?? 0;

    const componentChangeMap = new Map<string, any>();
    const serialsMap = new Map<string, any>();

    changeAnalysisData?.componentChanges.forEach((change: any) => {
      if (change?.componentDescription) {
        componentChangeMap.set(change.componentDescription, change);
      }
    });

    addSerialToMap(serialsMap, recommendation?.recommendedSerial, true);
    addSerialToMap(serialsMap, changeAnalysisData?.currentSerial, false);

    // The serial saved for this stand, guide and target profile is always shown (so it can show ticked), with its
    // saved components when it is not one of the current suggestions.
    const saved = savedSelections.find(
      (item) => item.standName === selectedStand && item.guideName?.toLowerCase().includes(type) && item.serialNumber,
    );
    if (saved?.serialNumber) {
      const existing = serialsMap.get(saved.serialNumber);
      serialsMap.set(
        saved.serialNumber,
        existing
          ? { ...existing, isSaved: true }
          : {
              code: saved.serialNumber,
              isRecommended: false,
              isSaved: true,
              serialComponents: saved.components
                .filter((component) => component.componentCode)
                .map((component) => ({
                  componentDescription: component.componentDescription,
                  componentCode: component.componentCode,
                })),
            },
      );
    }

    const uniqueSerials = Array.from(serialsMap.values());

    return (
      <ComponentColumn>
        <BlockHeader>{t(titleKey)}</BlockHeader>

        {uniqueSerials.length === 0 ? (
          <NoDataMessage>
            {activeStandTargetComponentData ? t('no_guide_serial_available') : t('no_stand_selected')}
          </NoDataMessage>
        ) : (
          <SuggestionsContainer>
            {uniqueSerials.map((item, index) => {
              const isExpanded = expandedSerialCode === item.code;
              // const hasSubComponents = item.serialComponents?.length > 0;
              // 1. Combine all unique component descriptions from both sub-components and changes list
              const allConsumableDesc = Array.from(
                new Set([
                  ...(item.serialComponents?.map((c: any) => c.componentDescription) || []),
                  ...Array.from(componentChangeMap.keys()),
                ]),
              );

              return (
                <SuggestionCard key={index}>
                  <SuggestionHeader
                    onClick={() => {
                      if (allConsumableDesc.length > 0) {
                        setExpandedSerialCode(isExpanded ? null : item.code);
                      }
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <input
                        type="checkbox"
                        style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                        checked={selectedSerial === item.code}
                        onChange={() => {
                          if (selectedSerial === item.code) {
                            setSelectedSerial(null);
                          } else {
                            setSelectedSerial(item.code);
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />

                      <div>
                        <GuideTitle>
                          Guide: <strong>{renderCodeCell(item.code)}</strong>
                        </GuideTitle>

                        {item.isRecommended && (
                          <span style={{ color: '#166534', fontWeight: 700, fontSize: '12px' }}>Recommended</span>
                        )}
                      </div>
                    </div>

                    <ChangeWarningTag>{totalChangesCount} change(s) required</ChangeWarningTag>
                  </SuggestionHeader>

                  {isExpanded && allConsumableDesc.length > 0 && (
                    <ComponentTable style={{ backgroundColor: '#fafafa', borderTop: '1px solid #e2e8f0' }}>
                      <tbody>
                        {allConsumableDesc.map((desc: string, subIdx: number) => {
                          const subComp = item.serialComponents?.find((c: any) => c.componentDescription === desc);
                          const changeInfo = componentChangeMap.get(desc);

                          // Determine if this component has an active change
                          const isChanged =
                            totalChangesCount > 0 && changeInfo && changeInfo.changeType !== 'Unchanged';

                          return (
                            <tr key={subIdx} style={{ backgroundColor: isChanged ? '#fff7ed' : '' }}>
                              <td className="label" style={{ paddingLeft: '24px' }}>
                                {desc}
                              </td>
                              <td className="code">
                                {isChanged ? (
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                    {changeInfo?.currentCode ? renderCodeCell(changeInfo.currentCode) : '-'}
                                    <span style={{ color: '#ea580c', fontWeight: 'bold' }}>&rarr;</span>
                                    {changeInfo?.requiredCode ? renderCodeCell(changeInfo.requiredCode) : '-'}
                                  </span>
                                ) : (
                                  renderCodeCell(subComp?.componentCode)
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </ComponentTable>
                  )}
                </SuggestionCard>
              );
            })}
          </SuggestionsContainer>
        )}
      </ComponentColumn>
    );
  };

  const renderTargetComponents = (guide: GuideRequirement | null | undefined, titleKey: string) => {
    return (
      <ComponentColumn>
        <BlockHeader>{t(titleKey)}</BlockHeader>
        {!guide ? (
          <NoDataMessage>
            {activeStandTargetComponentData ? t('no_guide_data_available') : t('no_stand_selected')}
          </NoDataMessage>
        ) : (
          <ComponentTable>
            <tbody>
              <tr>
                <td className="label">Guide Type</td>
                <td className="code">{renderCodeCell(guide.guideTypeCode)}</td>
              </tr>

              {guide.components?.map((component, index) => (
                <tr key={component.componentCode || index}>
                  <td className="label">{component.componentDescription}</td>
                  <td className="code">{renderCodeCell(component.componentCode)}</td>
                </tr>
              ))}
            </tbody>
          </ComponentTable>
        )}
      </ComponentColumn>
    );
  };

  return (
    <ThemeProvider themes={themes} selectedSize={size} selectedMode={themeMode}>
      <GuideOrderContainer style={style} className={className}>
        {pageLoading && <LoadingComponent />}
        <TopBarThemed topHeading={t('guide-preparation')} />

        {isInstalledPositionsEmpty ? (
          <ForcedSetupContainer>
            <ForcedSetupTitle>{t('no-installed-guides-configured')}</ForcedSetupTitle>
            <ForcedSetupText>{t('no-installed-guides-configured-description')}</ForcedSetupText>
            <Button type="primary" onClick={handleOpenInstalledPositionForm} prefixIcon={{ name: 'settings' }}>
              {t('manage-installed-guides')}
            </Button>
          </ForcedSetupContainer>
        ) : (
          <StandLayoutWrapper>
            <DashboardGrid>
              {/* LEFT COLUMN */}
              <DashboardColumn>
                {renderTargetComponents(activeStandTargetComponentData?.entryGuide, 'entry_guide_components')}
                {renderSuggestionsTable(activeStandSerialRecommendation?.entry, 'entry_guide_suggestions', 'entry')}
              </DashboardColumn>

              {/* CENTER COLUMN - Navigation Diagram */}
              <CenterIllustrationColumn>
                <StandNavigationHeader>{selectedStand || t('no_stand_selected')}</StandNavigationHeader>
                <img className="illustration-wrapper" src="rolling_mill.png" alt="Rolling Mill Illustration" />
              </CenterIllustrationColumn>

              {/* RIGHT COLUMN */}
              <DashboardColumn>
                {renderTargetComponents(activeStandTargetComponentData?.exitGuide, 'exit_guide_components')}
                {renderSuggestionsTable(activeStandSerialRecommendation?.exit, 'exit_guide_suggestions', 'exit')}
              </DashboardColumn>
            </DashboardGrid>

            {/* FAR RIGHT SIDEBAR CONTROLS */}
            <SidebarSection>
              <BlockHeader>{t('guide_configurations')}</BlockHeader>

              <FieldWrapper>
                <Label>{t('current_profile')}</Label>
                <Input value={currentProfile ?? ''} placeholder={t('current_profile')} readOnly />
              </FieldWrapper>

              <FieldWrapper>
                <Label>{t('target_profile')}</Label>
                <Select
                  value={targetProfile}
                  allowClear
                  placeholder={t('select_target_profile')}
                  onChange={handleTargetProfileSelection}
                >
                  {getProfileData.map((data) => (
                    <Select.Option key={data.productid} value={`${data.productdescription}`}>
                      {`${data.producttype} ${data.productdescription}`}
                    </Select.Option>
                  ))}
                </Select>
              </FieldWrapper>

              <FieldWrapper>
                <Label>{t('stand')}</Label>
                <Select
                  value={selectedStand}
                  allowClear
                  showSearch
                  placeholder={!targetProfile ? t('select_target_profile') : t('select_stand')}
                  onChange={handleStandSelection}
                  disabled={!targetProfile}
                >
                  {getAllGuideStand?.map((stand) => (
                    <Select.Option key={stand} value={stand}>
                      {stand}
                    </Select.Option>
                  ))}
                </Select>
              </FieldWrapper>

              <div className="sidebar-actions">
                <Button
                  onClick={handleOpenInstalledPositionForm}
                  prefixIcon={{ name: 'settings' }}
                  title={t('manage-installed-guides')}
                />

                {/* Saves the ticked entry/exit serials (unticked ones clear their saved selection) */}
                <Button
                  onClick={saveSerialSelection}
                  prefixIcon={{ name: 'save' }}
                  title={t('apply-serial-selection')}
                  disabled={!selectedStand || !targetProfile}
                />

                <Button
                  onClick={generateGuideReport}
                  prefixIcon={{ name: 'settings' }}
                  title={t('generate-guide-report')}
                  disabled={!selectedEntrySerial && !selectedExitSerial}
                />

                <Button
                  onClick={handleClearEntrySelection}
                  prefixIcon={{ name: 'settings' }}
                  title={t('clear-entry-selection')}
                  disabled={!selectedEntrySerial}
                />

                <Button
                  onClick={handleClearExitSelection}
                  prefixIcon={{ name: 'settings' }}
                  title={t('clear-exit-selection')}
                  disabled={!selectedExitSerial}
                />
              </div>
            </SidebarSection>
          </StandLayoutWrapper>
        )}

        <GuideInstalledPositionForm
          visible={showInstalledPositionForm}
          onClose={handleCloseInstalledPositionForm}
          onSaved={handleInstalledPositionsSaved}
        />

        {/* Guide Report Modal - Product Change report of the selected stand's serials and components */}
        <Modal
          title={t('generate-guide-report')}
          width={'75%'}
          visible={guideReportSrc.length > 0}
          onCancel={handleGuideReportCancel}
          footer={false}
          destroyOnClose
        >
          <PdfModalContent>
            <iframe src={guideReportSrc} title="Guide Report" />
          </PdfModalContent>
        </Modal>

        {/* PDF Modal */}
        <Modal
          title={`${t('consumable')} : ${selectedCode}`}
          width={'75%'}
          visible={showConsumableCodeDrawing}
          onCancel={handleDrawingModalCancel}
          footer={false}
          destroyOnClose
        >
          <PdfModalContent>
            <div className="pdf-toolbar">
              <PopConfirm
                title={<div>{t('delete-confirmation-text')}?</div>}
                onConfirm={handleDeletePDF}
                okText={t('delete')}
                cancelText={t('cancel')}
              >
                <Button prefixIcon={{ name: 'delete' }} disabled={!(iFrameSrc.trim().length > 0) || !isAdmin} />
              </PopConfirm>
            </div>
            <iframe src={iFrameSrc} title="PDF Viewer" />
          </PdfModalContent>
        </Modal>
      </GuideOrderContainer>
    </ThemeProvider>
  );
}

export default memo(GuideOrder) as unknown as typeof GuideOrder;
